import { useMemo, useReducer, type RefObject } from "react";
import { EnvironmentId } from "@cmmd-forge/contracts";
import { FolderIcon } from "lucide-react";

import { ProjectFavicon } from "~/components/ProjectFavicon";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxTrigger,
  useComboboxFilter,
} from "~/components/ui/combobox";
import { SidebarMenuButton } from "~/components/ui/sidebar";

import type { SidebarV2ProjectScopeEntry } from "./sidebarV2Controller";
import {
  filterSidebarProjectScopeItems,
  reduceSidebarProjectScopeMenuState,
} from "./sidebarV2Search";

interface ScopeItem {
  readonly value: string;
  readonly label: string;
}

/** Keep the project selector compact while its search menu uses the full header width. */
export function SidebarV2ProjectScope(input: {
  readonly projects: readonly SidebarV2ProjectScopeEntry[];
  readonly selectedKey: string | null;
  readonly onSelect: (key: string | null) => void;
  readonly anchorRef: RefObject<HTMLDivElement | null>;
}) {
  const [menu, dispatch] = useReducer(reduceSidebarProjectScopeMenuState, {
    open: false,
    query: "",
  });
  const filter = useComboboxFilter();
  const items = useMemo<readonly ScopeItem[]>(
    () => [
      { value: "all", label: "All projects" },
      ...input.projects.map((project) => ({
        value: project.projectKey,
        label: project.displayName,
      })),
    ],
    [input.projects],
  );
  const selected = items.find((item) => item.value === (input.selectedKey ?? "all")) ?? items[0]!;
  const selectedProject = input.projects.find(
    (project) => project.projectKey === input.selectedKey,
  );
  const filtered = filterSidebarProjectScopeItems({
    items,
    query: menu.query,
    matches: (item, query) => filter.contains(item, query, (candidate) => candidate.label),
  });

  return (
    <Combobox
      items={items}
      filteredItems={filtered}
      autoHighlight
      itemToStringLabel={(item) => item.label}
      isItemEqualToValue={(a, b) => a.value === b.value}
      open={menu.open}
      onOpenChange={(open) => dispatch({ type: "open-changed", open })}
      value={selected}
      onValueChange={(item) => {
        if (item) input.onSelect(item.value === "all" ? null : item.value);
      }}
    >
      <ComboboxTrigger
        render={
          <SidebarMenuButton
            size="icon"
            type="button"
            aria-label={
              selectedProject
                ? `Filter threads by project: ${selectedProject.displayName}`
                : "Filter threads by project"
            }
            className="relative size-7 shrink-0"
          />
        }
      >
        {selectedProject ? (
          <span className="flex shrink-0">
            <ProjectFavicon
              environmentId={EnvironmentId.make(selectedProject.environmentId)}
              cwd={selectedProject.cwd ?? ""}
              projectName={selectedProject.displayName}
              className="size-4"
            />
          </span>
        ) : (
          <FolderIcon className="size-4" />
        )}
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
        />
      </ComboboxTrigger>
      <ComboboxPopup
        align="start"
        anchor={input.anchorRef}
        className="max-w-[min(18rem,var(--available-width))] overflow-hidden"
      >
        <ComboboxInput
          aria-label="Search projects"
          placeholder="Search projects..."
          value={menu.query}
          onChange={(event) => dispatch({ type: "query-changed", query: event.target.value })}
          showTrigger={false}
          className="p-1"
        />
        <ComboboxEmpty>No matching projects.</ComboboxEmpty>
        <ComboboxList>
          {(item: ScopeItem) => {
            const project = input.projects.find((entry) => entry.projectKey === item.value);
            return (
              <ComboboxItem key={item.value} hideIndicator value={item}>
                <span className="flex min-w-0 items-center gap-2">
                  {project ? (
                    <ProjectFavicon
                      environmentId={EnvironmentId.make(project.environmentId)}
                      cwd={project.cwd ?? ""}
                      projectName={project.displayName}
                      className="size-4 shrink-0"
                    />
                  ) : (
                    <FolderIcon className="size-4 shrink-0" />
                  )}
                  <span className="min-w-0 truncate">{item.label}</span>
                </span>
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </ComboboxPopup>
    </Combobox>
  );
}
